import { ReactNode, useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import { COLORS, SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import IconCameraTorch from 'images/icons/inquiry/camera-torch.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import { useButtonSSTStyleContext } from 'inquiry/context/ButtonSSTStyleContext';
import FrameTrigger, { FrameTriggerConfig } from 'lib/clients/FrameTrigger';
import FrameTriggerClient from 'lib/clients/FrameTriggerClient';
import LivePhotoCaptureClient, { FRAME_INTERVAL } from 'lib/clients/LivePhotoCaptureClient';
import { VideoClient } from 'lib/clients/VideoClient';
import { useCameraInspection } from 'lib/hooks/useCameraInspection';
import useToggle from 'lib/hooks/useToggle';
import { captureException, captureMessage } from 'lib/reporting';
import { createCanvas } from 'lib/utils/canvas';
import { isInCypress } from 'lib/utils/dom';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import { bufferHorizontalImage } from 'verify/lib/utils/webcam';
import { DocumentFileOriginal } from 'verify/models/document-file';
import { CaptureMethod } from 'verify/models/verification';
import HintPrompt from 'verify/scanners/BaseScanner/HintPrompt';
import PreviewThumbnail from 'verify/scanners/BaseScanner/PreviewThumbnail';

interface VideoWrapperProps {
  mirrored: boolean;
  zoomFactor: number;
}
const VideoWrapper = styled('div')<VideoWrapperProps>`
  background: ${COLORS.black};
  overflow: hidden;
  position: relative;
  width: 100%;

  &::after {
    content: '';
    display: block;
    /* CSS trick to enforce an aspect ratio of 4:3 */
    padding-bottom: 75%;
  }

  video {
    position: absolute;
    top: 50%;

    transform-origin: top;
    transform: scaleX(${(props) => (props.mirrored ? -1 : 1) * (props.zoomFactor || 1)})
      scaleY(${(props) => props.zoomFactor || 1}) translateY(-50%);
  }
`;

const TorchButton = styled.button<{ active: boolean }>`
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  width: 40px;

  svg {
    height: 100%;
    width: 100%;
  }

  ${({ active }) =>
    active &&
    css`
      svg path {
        fill: white;
      }
    `}
`;

const HintWrapper = styled('div')<{ hasTorch: boolean; hasCaptures: boolean }>`
  background-color: ${COLORS.black};
  margin-bottom: ${SPACINGS.base * 2}px;
  padding: ${SPACINGS.base}px ${SPACINGS.base * 4}px;
  display: flex;
  align-items: center;

  ${({ hasTorch }) => hasTorch && `padding-right: ${SPACINGS.base}px;`}
  ${({ hasCaptures }) => hasCaptures && `padding-left: ${SPACINGS.base}px;`}
`;

interface BaseScannerProps<TriggerType, TriggerParams, Localizations = never> {
  handleCapture: ({
    imageSrcs,
    captureMethod,
    params,
    cameraInspectionData,
  }: {
    imageSrcs: string[];
    captureMethod: CaptureMethod;
    params: TriggerParams;
    cameraInspectionData: CameraInspectionData;
  }) => Promise<void>;
  handleFailed: (err: Error | string, state: 'load' | 'capture') => void;
  detectInterval: number;
  labelTakePhoto: string;
  videoConstraints: MediaTrackConstraints;
  /**
   * WARNING: This is used to initialize the frame trigger client in a ref.
   * Changing this prop at runtime will no-op.
   */
  triggerConfigs: FrameTriggerConfig<TriggerType, TriggerParams, Localizations>[];
  renderButton?: (args: {
    disabled: boolean;
    id: string;
    'data-test': string;
    onClick: () => void;
    children: ReactNode;
  }) => JSX.Element;
  paramsFromVideoFrame: (
    videoElement: HTMLVideoElement,
    canvasBuffer: HTMLCanvasElement,
    captureClient: LivePhotoCaptureClient,
    triggerClient: FrameTriggerClient<TriggerType, TriggerParams, Localizations>,
  ) => Promise<TriggerParams>;
  overlay?: JSX.Element;
  shouldSubmit: (
    params: TriggerParams,
    triggerClient: FrameTriggerClient<TriggerType, TriggerParams, Localizations>,
  ) => boolean;
  delayUntilManualCaptureMs?: number;
  triggerOverride?: FrameTrigger<TriggerType, TriggerParams, Localizations>;

  // Currently only DocumentGeneric uses DocumentFileOriginal but eventually all captures should use it.
  captures?: DocumentFileOriginal[];
  // TODO: [PER-8501] add error callback and check for permission denied errors
  //
  zoomFactor: number;
  scannerType: 'governmentId' | 'microblink' | 'barcode' | 'document';
  disableCaptureClient?: boolean;
}

export default function BaseScanner<TriggerType, TriggerParams, Localizations = never>(
  props: BaseScannerProps<TriggerType, TriggerParams, Localizations>,
) {
  const buttonStyles = useButtonSSTStyleContext();
  const timerDetect = useRef(undefined);
  const timerCapture = useRef(undefined);

  const webcam = useRef<Webcam & HTMLVideoElement>();
  const canvasBuffer = useRef<HTMLCanvasElement>();

  useEffect(() => {
    // Ensure this doesn't blow up if we try to access canvasBuffer.current too early.
    const [canvas, cleanup] = createCanvas();
    canvasBuffer.current = canvas;

    if (!props.disableCaptureClient) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      captureClient.current = new LivePhotoCaptureClient({ canvasBuffer: canvasBuffer.current });
    }

    return cleanup;
  }, []);

  const { logCameraInspectionFromStream, logCameraInspectionSnapshot, getCameraInspectionData } =
    useCameraInspection();

  const triggerClient = useRef(
    new FrameTriggerClient(props.triggerConfigs.map((config) => new FrameTrigger(config))),
  );
  const [activeTrigger, setActiveTrigger] = useState(triggerClient.current.getTriggered());

  const [isCameraReady, setIsCameraReady] = useState(false);
  const captureClient = useRef<LivePhotoCaptureClient>(null);
  const videoTrack = useRef<MediaStreamTrack>();

  const [torchLit, { toggle: toggleTorch }] = useToggle();
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [focusModeAvailable, setFocusModeAvailable] = useState(false);

  const [manualCaptureEnabled, setManualCaptureEnabled] = useState(
    props.delayUntilManualCaptureMs === 0 || isInCypress(),
  );

  const renderButton =
    props.renderButton ??
    ((args) => (
      <Button
        {...args}
        buttonType={ButtonType.Primary}
        styles={buttonStyles?.[ButtonType.Primary]}
      />
    ));

  useEffect(() => {
    let manualCaptureTimeout: number;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (props.delayUntilManualCaptureMs > 0) {
      manualCaptureTimeout = window.setTimeout(
        () => setManualCaptureEnabled(true),
        props.delayUntilManualCaptureMs,
      );
    }

    return () => {
      clearTimeout(manualCaptureTimeout);
    };
  }, []);

  const runCaptureClient = () => {
    // Video is null if webcam is unmounting
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (webcam.current?.video?.videoWidth > 0) {
      try {
        // Drawing video here can throw a transient NS_ERROR_NOT_AVAILBLE in some browsers.
        bufferHorizontalImage({
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          source: webcam.current.video,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          precreatedCanvas: canvasBuffer.current,
          zoomFactor: props.zoomFactor,
        });
        captureClient.current?.capture();
      } catch (err) {
        // NS_ERROR_NOT_AVAILABLE is thrown when the canvas isn't ready. Just wait in that case.
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        if (err.name !== 'NS_ERROR_NOT_AVAILABLE') {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          props.handleFailed(err, 'capture');
        }
      }
    }

    if (timerCapture.current != null) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timerCapture.current = window.setTimeout(runCaptureClient, FRAME_INTERVAL);
    }
  };

  const captureAndSubmit = async (captureMethod: CaptureMethod, params: TriggerParams) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (webcam.current.video) {
      bufferHorizontalImage({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        source: webcam.current.video,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        precreatedCanvas: canvasBuffer.current,
        zoomFactor: props.zoomFactor,
      });
      captureClient.current?.capture();

      if (!params) {
        params = await props.paramsFromVideoFrame(
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          webcam.current.video,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          canvasBuffer.current,
          captureClient.current,
          triggerClient.current,
        );
      }

      await props.handleCapture({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        imageSrcs: captureClient.current?.leastBlurryImages(),
        captureMethod,
        params,
        cameraInspectionData: getCameraInspectionData({ includeSmallImages: false }),
      });
    }
  };

  // Runs the "tick" for autocapture. A tick is run every DETECT_INTERVAL.
  // However, because the next tick is not scheduled until processing is
  // finished, DETECT_INTERVAL represents the MINIMUM time between ticks. There
  // should never be more than 1 tick being evaluated at once.
  //
  // WARNING: Because this function is defined inline and used in a setTimeout,
  // state values accessed in this function will always be stale. Use refs
  // instead.
  const runTriggerClient = async () => {
    const startTime = new Date().getTime();

    // Video is null if webcam is unmounting
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (webcam.current?.video?.videoWidth > 0) {
      const params = await props.paramsFromVideoFrame(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        webcam.current.video,
        canvasBuffer.current,
        captureClient.current,
        triggerClient.current,
      );

      logCameraInspectionSnapshot(canvasBuffer.current);

      triggerClient.current.runTick(params);
      setActiveTrigger(triggerClient.current.getTriggered());

      if (props.shouldSubmit(params, triggerClient.current)) {
        captureAndSubmit(CaptureMethod.Auto, params);

        // Stop capture
        return;
      }
    }

    // If the old timeout was cancelled while in this async function, don't run a new tick
    if (timerDetect.current != null) {
      const nextFrameDelay = Math.max(0, props.detectInterval - (new Date().getTime() - startTime));
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timerDetect.current = window.setTimeout(runTriggerClient, nextFrameDelay);
    }
  };

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    timerDetect.current = window.setTimeout(runTriggerClient, props.detectInterval);
    if (!props.disableCaptureClient) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timerCapture.current = window.setTimeout(runCaptureClient, FRAME_INTERVAL);
    }

    return () => {
      clearTimeout(timerDetect.current);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timerDetect.current = null;
      clearTimeout(timerCapture.current);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      timerCapture.current = null;

      VideoClient.dispose();
    };
  }, []);

  useEffect(() => {
    try {
      // Prevent OperationError: the associated track is in an invalid state
      if (videoTrack.current?.readyState === 'live' && torchAvailable) {
        const constraints: MediaTrackConstraintSet = { torch: torchLit };

        if (focusModeAvailable) {
          // Need to reapply focusMode; does not auto-merge constraint configs.
          constraints.focusMode = 'continuous';
        }

        videoTrack.current.applyConstraints({ advanced: [constraints] });
      }
    } catch (error) {
      captureMessage('Failed to enable torch', 'warning', { error });
    }
  }, [torchLit]);

  const setRefWebcam = (newRef: Webcam & HTMLVideoElement) => {
    if (newRef) {
      webcam.current = newRef;
      // There is a longstanding React bug where the muted attribute is not
      // propagated to the DOM <video> element. This can cause permissions
      // related issues.
      // Sample fix: https://stackoverflow.com/a/66153842
      // Github issue: https://github.com/facebook/react/issues/6544#issuecomment-705702546
      // Remove if upstream fix merged: https://github.com/mozmorris/react-webcam/issues/332
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      webcam.current.video.defaultMuted = true;
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      webcam.current.video.muted = true;

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      webcam.current.video.addEventListener('loadedmetadata', async () => {
        // NOTE: do NOT call getUserMedia here, it causes video to fail in iOS Safari
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const stream = webcam.current.stream;
        // If ImageCapture isn't supported, then torch won't be either
        if (stream && typeof ImageCapture !== 'undefined') {
          const track = stream.getVideoTracks()[0];
          // PER-21286
          if (track == null) {
            props.handleFailed('Video track is null', 'load');
            return;
          }

          // Prevent OperationError: the associated track is in an invalid state
          if (track.readyState !== 'live') {
            return;
          }

          try {
            // NOTE: must call these together. Torch config in mediaCapabilities
            // is filled when imageCapture.getPhotoCapabilities() is called.
            await new ImageCapture(track).getPhotoCapabilities();

            // The user may have navigated away while getting photo capabilities.
            // Check readyState again.
            if (track.readyState !== 'live') {
              return;
            }

            const mediaCapabilities = track.getCapabilities();

            if (track.applyConstraints.toString().includes('function')) {
              if (mediaCapabilities.focusMode != null) {
                // Enable continuous autofocus if available
                track.applyConstraints({
                  advanced: [
                    {
                      focusMode: 'continuous',
                    },
                  ],
                });
              }

              videoTrack.current = track;
              setTorchAvailable(mediaCapabilities.torch != null);
              setFocusModeAvailable(mediaCapabilities.focusMode != null);
            }
          } catch (error) {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            if (error.name !== 'OperationError') {
              // See PER-5997 OperationError means we couldn't access the track. Do nothing, as torch is optional.
              captureException(error, 'warning');
            }
          }
        }
      });

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      webcam.current.video.addEventListener('canplay', async () => {
        // Navigating away can cause this event to fire when video is null
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        if (webcam.current.video) {
          setIsCameraReady(true);
          // video autoplay / play permissioning is all over the place.
          // if the webcam is in 'canplay' status but it's paused,
          // let's usher it to play.
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          if (webcam.current.video.paused) {
            try {
              // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play
              // Some browsers will prompt for permission on (auto)play.
              // Other browsers will fail immediately (e.g. if the user blocks autoplay in settings).
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              await webcam.current.video.play();
            } catch (e) {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              props.handleFailed(e, 'load');
            }
          }
        }
      });
    }
  };

  return (
    <div data-test='scanner' data-scanner-type={props.scannerType}>
      <VideoWrapper
        mirrored={props.videoConstraints.facingMode === 'user'}
        zoomFactor={props.zoomFactor}
      >
        <Webcam
          audio={false}
          width='100%'
          height='initial'
          ref={setRefWebcam}
          data-test='base-scanner__verify'
          videoConstraints={props.videoConstraints}
          onUserMedia={logCameraInspectionFromStream}
          onUserMediaError={(error) => props.handleFailed(error, 'load')}
        />

        {props.overlay}
      </VideoWrapper>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <HintWrapper hasCaptures={props.captures?.length > 0} hasTorch={torchAvailable}>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <PreviewThumbnail files={props.captures} />
        <HintPrompt activeTrigger={props.triggerOverride ?? activeTrigger} color={COLORS.white} />
        {torchAvailable && (
          <TorchButton
            id='scanner__button--torch'
            onClick={toggleTorch}
            active={torchLit}
            title={torchLit ? 'Flash ON' : 'Flash OFF'}
          >
            <IconCameraTorch />
          </TorchButton>
        )}
      </HintWrapper>
      {renderButton({
        disabled: !isCameraReady || !manualCaptureEnabled,
        id: 'scanner__button--capture',
        'data-test': 'scanner__button--capture',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onClick: () => captureAndSubmit(CaptureMethod.Manual, null),
        children: props.labelTakePhoto,
      })}
      <Spacer height={1} />
    </div>
  );
}
